/**
 * useCamera — hook around getUserMedia for BOSCO Capture.
 *
 * v90.1 — Hard-lock the REAR camera and fix the "black screen with red
 * frame" bug reported on Android + iOS.
 *
 * Root causes of the bug that used to ship:
 *   1. `facingMode: { ideal: 'environment' }` is a SOFT constraint on
 *      Chromium-Android and Safari-iOS. The OS was free to hand back
 *      either the front sensor OR a virtual "system" track (all-black),
 *      which is exactly what users saw — video srcObject wired up
 *      correctly, corner frame drawn, sensor never lit.
 *   2. `width: 1280, height: 1920` (portrait, ~2.4MP) is NOT a native
 *      sensor mode on any real phone camera. The constraint negotiator
 *      failed the "hard" case then silently fell back to a black stub.
 *
 * Fixes:
 *   • `facingMode: { exact: 'environment' }` — HARD constraint. If a
 *     device has no rear camera we surface a clean error instead of
 *     silently activating the front camera. This satisfies the product
 *     directive: BOSCO Capture must NEVER activate the front camera.
 *   • Request the sensor's NATIVE landscape orientation
 *     (1920×1080 / 1280×720) and render portrait via CSS transform on
 *     the video element. This is what every real camera app does.
 *   • Wait for `loadedmetadata` before calling play() — Safari-iOS
 *     rejects play() if the media isn't yet ready. This is what caused
 *     the intermittent "permission granted but nothing renders" cases.
 *   • enumerateDevices() diagnostics: if getUserMedia succeeds but the
 *     resulting track's `facingMode` label doesn't match 'environment',
 *     we stop the stream and re-open with an alternate deviceId that
 *     matches /back|rear|environment/ in the label. Belt-and-braces.
 *   • The old `flip()` API is preserved for backward compat but is now
 *     a no-op that ALWAYS returns to 'environment' — never surfaces the
 *     front camera.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// Preferred landscape resolutions — descending. The first one the
// device supports wins. Cameras natively capture landscape; portrait
// rendering is CSS, not a media constraint.
const RESOLUTION_TIERS = [
  { width: 1920, height: 1080 },
  { width: 1280, height: 720  },
  { width: 640,  height: 480  },
];

export function useCamera() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [stream, setStream] = useState(null);        // v90.2 — declarative binding
  const [facing] = useState('environment'); // hard-locked; front is forbidden
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
        streamRef.current = null;
      }
      setStream(null);
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch { /* ignore */ }
  }, []);

  // Wait for the <video> element to publish real dimensions before we
  // call play() — iOS Safari REJECTS play() if metadata isn't ready.
  // v90.2 — Moved into the view's useEffect (declarative bind). Retained
  // here as a private helper in case future callers want an imperative
  // path. Currently unused; safe to remove if you're sure.
  const waitForMetadata = (video) => new Promise((resolve) => {
    if (!video) return resolve();
    if (video.readyState >= 1 && video.videoWidth > 0) return resolve();
    const onReady = () => {
      video.removeEventListener('loadedmetadata', onReady);
      resolve();
    };
    video.addEventListener('loadedmetadata', onReady, { once: true });
    // Safety timeout so we don't wedge forever on odd browsers.
    setTimeout(resolve, 1500);
    return undefined;
  });

  // Try to acquire a stream with a specific constraint set; returns the
  // MediaStream on success, throws on failure.
  const _acquire = async (extra) => {
    const tries = RESOLUTION_TIERS.map((r) => ({
      audio: false,
      video: {
        // HARD-lock rear sensor. If missing we throw OverconstrainedError
        // and NEVER silently fall back to the user camera.
        facingMode: { exact: 'environment' },
        width:  { ideal: r.width  },
        height: { ideal: r.height },
        ...(extra || {}),
      },
    }));
    let lastErr = null;
    for (const c of tries) {
      try { return await navigator.mediaDevices.getUserMedia(c); }
      catch (e) { lastErr = e; }
    }
    throw lastErr || new Error('Camera acquisition failed');
  };

  // After we have a track, verify it's actually the rear camera by
  // reading track settings + label. Some devices misreport facingMode
  // in settings but the label always contains "back" or "rear".
  const _isRearTrack = (track) => {
    if (!track) return false;
    const s = track.getSettings?.() || {};
    if (s.facingMode === 'environment') return true;
    const label = (track.label || '').toLowerCase();
    if (/back|rear|environment|world/.test(label)) return true;
    if (/front|user|face|selfie/.test(label)) return false;
    // Unknown label — accept only if facingMode is explicitly not user.
    return s.facingMode !== 'user';
  };

  // If we accidentally got a non-rear track, enumerate devices and
  // reopen with an exact deviceId matching a rear label.
  const _reopenWithDeviceId = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const rear = devices.find((d) =>
        d.kind === 'videoinput'
        && /back|rear|environment|world/.test((d.label || '').toLowerCase())
      );
      if (!rear) return null;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { deviceId: { exact: rear.deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      return stream;
    } catch {
      return null;
    }
  };

  const start = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setStatus('error'); setError('Camera is not supported on this browser.');
      return false;
    }
    // HTTPS gate — getUserMedia fails silently on http://, better to say so up front.
    if (typeof window !== 'undefined'
        && window.isSecureContext === false) {
      setStatus('error'); setError('Camera requires HTTPS. Open the site over https:// and try again.');
      return false;
    }
    setStatus('prompting'); setError(null);
    stop();
    try {
      let stream = await _acquire();
      let track = stream.getVideoTracks()[0];
      // If the driver handed us a non-rear track despite `exact`, retry
      // via enumerateDevices+deviceId. If that also fails, throw so we
      // land in the NotFoundError branch — we do NOT silently fall
      // back to the front camera.
      if (!_isRearTrack(track)) {
        stream.getTracks().forEach((t) => t.stop());
        const alt = await _reopenWithDeviceId();
        if (!alt) {
          throw Object.assign(new Error('No rear camera available on this device.'), { name: 'NotFoundError' });
        }
        stream = alt;
        track = stream.getVideoTracks()[0];
        if (!_isRearTrack(track)) {
          stream.getTracks().forEach((t) => t.stop());
          throw Object.assign(new Error('No rear camera available on this device.'), { name: 'NotFoundError' });
        }
      }
      streamRef.current = stream;
      // v90.2 — Do NOT try to attach srcObject here. The <video> element
      // is only rendered by the parent view AFTER setStatus('granted')
      // triggers a re-render, so videoRef.current is null right now.
      // Expose the MediaStream via React state and let the view bind it
      // declaratively via useEffect — no more null-ref race.
      setStream(stream);
      setStatus('granted');
      return true;
    } catch (e) {
      const name = e?.name || '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setStatus('denied');
        setError('Camera permission denied. Enable camera access for this site in your browser settings, then try again.');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') {
        setStatus('error');
        setError('BOSCO Capture needs a rear camera. Please open this on a phone with a back camera (browser: Chrome on Android or Safari on iOS).');
      } else {
        setStatus('error'); setError(e?.message || 'Could not start camera.');
      }
      return false;
    }
  }, [stop]);

  // Backward-compat no-op — the old flip UI has been removed but any
  // stray call must NEVER surface the front camera per product spec.
  const flip = useCallback(async () => start(), [start]);

  useEffect(() => () => stop(), [stop]);

  return { videoRef, stream, status, error, facing, start, stop, flip };
}
